import { readJsonPref, removePref, writeJsonPref } from '../ui/prefs';
import { readConsent } from './consent';
import { getInstallId, isUuid, makeUuid } from './identity';
import { currentWorkerBase } from './unlock';
import type { TelemetryChannel } from './types';

/**
 * Événements du mur payant (A112) : « mur affiché », « achat commencé / réussi /
 * raté », « code accepté / refusé »… Un par geste, mis en file locale
 * (`localStorage`, 50 au plus) **seulement si le consentement aux statistiques
 * anonymes est donné**, puis envoyés par lots de 20 à `POST {base}/v1/events`.
 *
 * **Jamais bloquant** (A79) : rien de synchrone au lancement hors la lecture de
 * `localStorage`, toute erreur est avalée, un hors-ligne ou un `503` garde la
 * file pour plus tard. Aucune donnée de paiement ne passe ici : Google Play ne
 * parle jamais au Worker. Protocole : `docs/contracts/CONTRAT_A112_WORKER.md` §3.
 */

/**
 * Genres d'événements (même liste que `EVENT_KINDS` du Worker). A118 : `ack_ok` (achat Play acquitté,
 * détail `acknowledged` | `already`), `ack_failed` (refus définitif du Worker, détail = raison) et
 * `revoked` (appareil refermé, détail `play_missing`).
 */
export const GATE_EVENT_KINDS = [
  'gate_shown',
  'gate_dismissed',
  'purchase_started',
  'purchase_ok',
  'purchase_failed',
  'restore_ok',
  'code_ok',
  'code_refused',
  'ack_ok',
  'ack_failed',
  'revoked',
] as const;
export type GateEventKind = (typeof GATE_EVENT_KINDS)[number];

export interface GateEvent {
  eventId: string;
  kind: GateEventKind;
  at: string;
  version: string;
  channel: TelemetryChannel;
  saveId?: string;
  level?: number;
  detail?: string;
}

export const EVENTS_KEY = 'diceordie.telemetry.events';
/** Plafond de la file locale : les plus anciens tombent. */
export const EVENTS_MAX = 50;
/** Taille d'un lot. */
export const EVENTS_BATCH = 20;
export const EVENTS_PATH = '/v1/events';
export const EVENTS_TIMEOUT_MS = 8000;
/** Même délai que l'envoi de lancement de la télémétrie (`LAUNCH_DELAY_MS`), sans importer son module. */
export const GATE_LAUNCH_DELAY_MS = 3000;
const DETAIL_MAX = 64;

/** Variable de build : clé partagée facultative, envoyée en `x-dod-key` comme les rapports. */
const BUILD_KEY = (import.meta.env.VITE_TELEMETRY_KEY as string | undefined) ?? undefined;

// ──────────────────────────────────────────────────────────────── Contexte

export interface GateEventsContext {
  version: string;
  channel: TelemetryChannel;
  /** Identifiant de la partie courante (relu à chaque événement). */
  saveId?: () => string | undefined;
  /** Faux pendant un combat : aucun envoi n'est lancé (comme la télémétrie). */
  canSend?: () => boolean;
}

let context: GateEventsContext = { version: '0', channel: 'prod' };

/** Pose version, canal, partie courante (`installEntitlement`). */
export function configureGateEvents(ctx: GateEventsContext): void {
  context = ctx;
}

// ───────────────────────────────────────────────────────────────────── File

function isKind(v: unknown): v is GateEventKind {
  return typeof v === 'string' && (GATE_EVENT_KINDS as readonly string[]).includes(v);
}

function readQueue(): GateEvent[] {
  const raw = readJsonPref<unknown>(EVENTS_KEY);
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is GateEvent =>
      typeof e === 'object' && e !== null && typeof (e as GateEvent).eventId === 'string' && isKind((e as GateEvent).kind),
  );
}

function writeQueue(list: GateEvent[]): void {
  if (list.length === 0) removePref(EVENTS_KEY);
  else writeJsonPref(EVENTS_KEY, list.slice(-EVENTS_MAX));
}

/** Nombre d'événements en attente d'envoi. */
export function pendingGateEvents(): number {
  return readQueue().length;
}

/** Vide la file (tests, debug). */
export function clearGateEvents(): void {
  removePref(EVENTS_KEY);
}

/**
 * Empile un événement du mur. Sans consentement, rien n'est gardé. L'envoi
 * suit aussitôt, en ligne et hors combat, sans jamais bloquer ni lever.
 */
export function trackGateEvent(kind: GateEventKind, extra?: { detail?: string; level?: number }): void {
  try {
    if (!readConsent()) return;
    const event: GateEvent = {
      eventId: makeUuid(),
      kind,
      at: new Date().toISOString(),
      version: context.version,
      channel: context.channel,
    };
    let saveId: string | undefined;
    try {
      saveId = context.saveId?.();
    } catch {
      saveId = undefined;
    }
    if (isUuid(saveId)) event.saveId = saveId;
    if (typeof extra?.level === 'number' && Number.isFinite(extra.level)) event.level = Math.max(0, Math.floor(extra.level));
    if (extra?.detail) event.detail = String(extra.detail).slice(0, DETAIL_MAX);
    writeQueue([...readQueue(), event]);
    void flushGateEvents().catch(() => undefined);
  } catch {
    /* le mur ne doit jamais gêner le jeu (A79) */
  }
}

// ──────────────────────────────────────────────────────────────────── Envoi

export type GateFlushResult =
  | { result: 'ok'; sent: number; rejected: number; remaining: number }
  | { result: 'disabled' | 'noUrl' | 'offline' | 'inCombat' | 'busy' | 'empty'; sent: 0; rejected: 0; remaining: number }
  | { result: `error:${string}`; sent: number; rejected: number; remaining: number };

export type EventsFetch = (
  url: string,
  init: {
    method: 'POST';
    headers: Record<string, string>;
    body: string;
    signal: AbortSignal;
    credentials: 'omit';
  },
) => Promise<{ ok: boolean; status: number }>;

export interface FlushOptions {
  fetchFn?: EventsFetch;
  /** Racine du Worker ; par défaut `currentWorkerBase()`. */
  base?: string | null;
  installId?: string;
  key?: string;
  online?: boolean;
  timeoutMs?: number;
}

let flushing = false;

/**
 * Envoie la file par lots de 20. `200` : les envoyés sont retirés ; `400` /
 * `413` : rejet définitif, le lot est retiré aussi ; erreur réseau, délai,
 * `503` et autres : la file est gardée. Ne lève jamais.
 */
export async function flushGateEvents(opts: FlushOptions = {}): Promise<GateFlushResult> {
  const left = (): number => readQueue().length;
  try {
    if (!readConsent()) {
      // Consentement retiré : plus rien n'est conservé (même règle que la télémétrie).
      clearGateEvents();
      return { result: 'disabled', sent: 0, rejected: 0, remaining: 0 };
    }
    if (flushing) return { result: 'busy', sent: 0, rejected: 0, remaining: left() };
    if (left() === 0) return { result: 'empty', sent: 0, rejected: 0, remaining: 0 };
    const base = opts.base === undefined ? currentWorkerBase() : opts.base;
    if (!base) return { result: 'noUrl', sent: 0, rejected: 0, remaining: left() };
    const online = opts.online ?? !(typeof navigator !== 'undefined' && navigator.onLine === false);
    if (!online) return { result: 'offline', sent: 0, rejected: 0, remaining: left() };
    if (context.canSend && !context.canSend()) return { result: 'inCombat', sent: 0, rejected: 0, remaining: left() };

    flushing = true;
    const fetchFn: EventsFetch =
      opts.fetchFn ?? ((url, init) => fetch(url, init as RequestInit) as unknown as ReturnType<EventsFetch>);
    const installId = opts.installId ?? getInstallId();
    const key = opts.key ?? BUILD_KEY;
    let sent = 0;
    let rejected = 0;
    try {
      // La file est bornée (50) : la boucle l'est aussi.
      for (let guard = 0; guard < 10; guard++) {
        const batch = readQueue().slice(0, EVENTS_BATCH);
        if (batch.length === 0) break;
        const headers: Record<string, string> = { 'content-type': 'application/json' };
        if (key) headers['x-dod-key'] = key;
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? EVENTS_TIMEOUT_MS);
        let status: number;
        try {
          const res = await fetchFn(`${base}${EVENTS_PATH}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ installId, events: batch }),
            signal: ctl.signal,
            credentials: 'omit',
          });
          status = res.ok ? 200 : res.status;
        } catch (err) {
          const timeout = ctl.signal.aborted || (err as { name?: string } | null)?.name === 'AbortError';
          return { result: timeout ? 'error:timeout' : 'error:network', sent, rejected, remaining: left() };
        } finally {
          clearTimeout(timer);
        }
        if (status === 200 || status === 400 || status === 413) {
          // Retirés de la file **courante** (d'autres événements ont pu y entrer pendant l'envoi).
          const gone = new Set(batch.map((e) => e.eventId));
          writeQueue(readQueue().filter((e) => !gone.has(e.eventId)));
          if (status === 200) sent += batch.length;
          else rejected += batch.length;
          continue;
        }
        return { result: `error:${status}`, sent, rejected, remaining: left() };
      }
      return { result: 'ok', sent, rejected, remaining: left() };
    } finally {
      flushing = false;
    }
  } catch {
    flushing = false;
    return { result: 'error:internal', sent: 0, rejected: 0, remaining: 0 };
  }
}

// ────────────────────────────────────────────────── Déclencheurs (lancement)

let installed: { launch: ReturnType<typeof setTimeout>; onOnline: () => void } | null = null;

/**
 * Branche les envois de fond : un après `GATE_LAUNCH_DELAY_MS`, un au retour de
 * la connexion (`online`). Idempotent. Renvoie le débranchement (tests).
 */
export function installGateEventsFlush(delayMs: number = GATE_LAUNCH_DELAY_MS): () => void {
  if (typeof window === 'undefined') return () => undefined;
  if (installed) return () => undefined;
  const run = (): void => {
    void flushGateEvents().catch(() => undefined);
  };
  const launch = setTimeout(run, delayMs);
  window.addEventListener('online', run);
  installed = { launch, onOnline: run };
  return () => {
    if (!installed) return;
    clearTimeout(installed.launch);
    window.removeEventListener('online', installed.onOnline);
    installed = null;
  };
}
